
import {
  createProvider,
  createUserModel,
  deleteProvider,
  deleteUserModel,
  getAgentSettings,
  listModels,
  listProviders,
  listUserModels,
  updateAgentSettings,
  type AgentSettings,
  type ModelSummary,
  type ProviderSummary,
  type UserModelSummary,
} from "../api/settings.js";

const DEFAULT_AGENT_SETTINGS: AgentSettings = {
  subagent_allowed_models: [],
  subagent_guidelines: "",
  using_server_defaults: true,
  server_default_guidelines: "",
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

type AccountView = { id: string; name: string; meta: string };

type ModelItemView = { id: string; name: string; meta: string; badge?: string };

function modelSettingsMeta(model: {
  description: string;
  slug: string;
  provider: string;
  vision: boolean;
}): string {
  if (model.description.trim()) {
    return model.description;
  }
  return `${model.slug} - ${model.provider}${model.vision ? " - vision" : ""}`;
}

function configModelView(model: ModelSummary): ModelItemView {
  return {
    id: model.key,
    name: escapeHtml(model.display_name),
    meta: escapeHtml(modelSettingsMeta(model)),
    badge: "Server",
  };
}

function userModelItemView(model: UserModelSummary): AccountView {
  return {
    id: model.id,
    name: escapeHtml(model.display_name),
    meta: escapeHtml(
      modelSettingsMeta({
        description: model.description,
        slug: model.slug,
        provider: model.provider_name,
        vision: model.vision,
      }),
    ),
  };
}

type SubagentModelView = { key: string; label: string; checked: boolean };

function subagentModelView(model: ModelSummary, allowed: string[]): SubagentModelView {
  return {
    key: model.key,
    label: escapeHtml(model.display_name),
    checked: allowed.includes(model.key),
  };
}

async function submitProvider(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  await createProvider({
    name: String(data.get("name") ?? "").trim(),
    kind: String(data.get("kind") ?? "").trim(),
    url: String(data.get("url") ?? "").trim(),
    token: String(data.get("token") ?? "").trim(),
  });
}

async function submitUserModel(form: HTMLFormElement, vision: boolean): Promise<void> {
  const data = new FormData(form);
  await createUserModel({
    name: String(data.get("name") ?? "").trim(),
    slug: String(data.get("slug") ?? "").trim(),
    provider_id: String(data.get("provider_id") ?? "").trim(),
    display_name: String(data.get("display_name") ?? "").trim() || null,
    description: String(data.get("description") ?? "").trim() || null,
    reasoning_effort: String(data.get("reasoning_effort") ?? "").trim() || null,
    vision,
  });
}

const styles = css`
  .model-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .model-item {
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 8px;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 12px;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
  }

  .desc,
  .muted {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  form {
    display: grid;
    gap: 14px;
  }

  .grid {
    display: grid;
    gap: 14px;
    grid-template-columns: 1fr 1fr;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    font-weight: 500;
    gap: 6px;
  }

  label.full {
    grid-column: 1 / -1;
  }

  input,
  select,
  textarea {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    outline: none;
    padding: 8px 10px;
    transition:
      border-color 140ms ease,
      box-shadow 140ms ease;
    width: 100%;
  }

  input,
  select {
    height: 36px;
  }

  textarea {
    min-height: 84px;
    resize: vertical;
  }

  .skill-content textarea {
    font-family:
      ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    min-height: 200px;
  }

  input:focus,
  select:focus,
  textarea:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--muted-foreground);
  }

  .actions app-button,
  .model-item app-button {
    width: auto;
  }

  .checkbox-row {
    align-items: center;
    display: flex;
    gap: 10px;
  }

  .checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .saved {
    color: var(--muted-foreground);
    font-size: 13px;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
  }

  .error:empty {
    display: none;
  }

  @media (max-width: 767px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
`;

export class AppSettingsModels extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsModels)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #availableModels;
  #providers;
  #userModels;
  #loaded;
  #error;
  #agentSettings;
  #agentSettingsError;
  #agentSettingsSaved;
  #modelVision;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textStart4;
  #textEnd4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #attr0;
  #attrLast0;
  #lists = {};
  #eventContexts = {};
  #guidelinesArea = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="guidelinesArea"]') : null; } }; })();
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setAvailableModels(value) {
    if (Object.is(this.#availableModels, value)) return;
    this.#availableModels = value;
    this.#updateBindings('availableModels');
  }

  #setProviders(value) {
    if (Object.is(this.#providers, value)) return;
    this.#providers = value;
    this.#updateBindings('providers');
  }

  #setUserModels(value) {
    if (Object.is(this.#userModels, value)) return;
    this.#userModels = value;
    this.#updateBindings('userModels');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #setAgentSettings(value) {
    if (Object.is(this.#agentSettings, value)) return;
    this.#agentSettings = value;
    this.#updateBindings('agentSettings');
  }

  #setAgentSettingsError(value) {
    if (Object.is(this.#agentSettingsError, value)) return;
    this.#agentSettingsError = value;
    this.#updateBindings('agentSettingsError');
  }

  #setAgentSettingsSaved(value) {
    if (Object.is(this.#agentSettingsSaved, value)) return;
    this.#agentSettingsSaved = value;
    this.#updateBindings('agentSettingsSaved');
  }

  #setModelVision(value) {
    if (Object.is(this.#modelVision, value)) return;
    this.#modelVision = value;
    this.#updateBindings('modelVision');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ])
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to load model settings.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }


  #initState() {
    this.#availableModels = [];
    this.#providers = [];
    this.#userModels = [];
    this.#loaded = false;
    this.#error = "";
    this.#agentSettings = DEFAULT_AGENT_SETTINGS;
    this.#agentSettingsError = "";
    this.#agentSettingsSaved = false;
    this.#modelVision = false;
  }

  #render() {
    const configModelViews = this.#availableModels.filter((model) => model.source === "config").map(configModelView);
    const providerViews = this.#providers.map((provider) => ({
    id: provider.id,
    name: escapeHtml(provider.name),
    meta: escapeHtml(`${provider.kind} - ${provider.url}`),
}));
    const userModelViews = this.#userModels.map(userModelItemView);
    const subagentModelViews = this.#availableModels.map((model) => subagentModelView(model, this.#agentSettings.subagent_allowed_models));
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Server models" description="Add chat models in config.toml under [models.&lt;key&gt;]. Set display_name for labels in the composer and description for this list. Reserved keys embeddings, transcription, title_generator, expert, and explorer are internal and not shown here."><!--argon-text:0-->${(configModelViews.length > 0) ? `<div class="model-list">${configModelViews.map((model) => `<div class="model-item"><div><div class="name">${model.name}</div><div class="desc">${model.meta}</div></div><app-badge variant="secondary">${model.badge}</app-badge></div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No server models are configured." : "Loading models...")}</p>`}<!--/argon-text:0--><p class="muted">Example: duplicate a [models.*] block in config.toml.example, set display_name and description, then restart the server.</p></app-settings-section><app-settings-section title="Providers" description="Add your own LLM provider credentials. Models you define below will use these providers."><!--argon-text:1-->${(providerViews.length > 0) ? `<div class="model-list">${providerViews.map((provider, __argonIndex) => this.#mapEventItem(`<div class="model-item"><div><div class="name">${provider.name}</div><div class="desc">${provider.meta}</div></div><app-button variant="outline" size="sm" data-argon="8550">Remove</app-button></div>`, "initial:2:8550:" + __argonIndex, { 8550: () => {
    if (!window.confirm("Remove this provider and its models?"))
        return;
    void deleteProvider(provider.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove provider.");
    });
} }, ["8550"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No personal providers yet." : "Loading providers...")}</p>`}<!--/argon-text:1--><form data-argon="9858"><div class="grid"><label>Name<input name="name" required placeholder="my_openai" autocomplete="off" pattern="[A-Za-z0-9_](?:[A-Za-z0-9_]|-)*"></label><label>Kind<select name="kind" required><option value="openai">OpenAI</option><option value="openrouter">OpenRouter</option><option value="anthropic">Anthropic</option><option value="ollama">Ollama</option><option value="ollama_cloud">Ollama Cloud</option></select></label><label class="full">URL<input name="url" type="url" required placeholder="https://api.openai.com/v1" autocomplete="off"></label><label class="full">API token<input name="token" type="password" required autocomplete="off"></label></div><div class="actions"><app-button type="submit">Add provider</app-button></div></form></app-settings-section><app-settings-section title="Personal models" description="Define models that use your providers. The registry key is internal; display_name is shown in the composer and description appears here."><!--argon-text:2-->${(userModelViews.length > 0) ? `<div class="model-list">${userModelViews.map((model, __argonIndex) => this.#mapEventItem(`<div class="model-item"><div><div class="name">${model.name}</div><div class="desc">${model.meta}</div></div><app-button variant="outline" size="sm" data-argon="12479">Remove</app-button></div>`, "initial:3:12479:" + __argonIndex, { 12479: () => {
    if (!window.confirm("Remove this model?"))
        return;
    void deleteUserModel(model.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove model.");
    });
} }, ["12479"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No personal models yet." : "Loading models...")}</p>`}<!--/argon-text:2--><form data-argon="13758"><div class="grid"><label>Registry key<input name="name" required placeholder="my_sonnet" autocomplete="off" pattern="[A-Za-z0-9_](?:[A-Za-z0-9_]|-)*"></label><label>Display name<input name="display_name" placeholder="Claude Sonnet" autocomplete="off"></label><label class="full">Description<textarea name="description" placeholder="When to use this model." rows="2"></textarea></label><label>Model slug<input name="slug" required placeholder="claude-sonnet-4-20250514" autocomplete="off"></label><label>Provider<select name="provider_id" required><option value="">Select provider</option><!--argon-text:3-->${providerViews.map((provider) => `<option value="${provider.id}">${provider.name}</option>`).join("")}<!--/argon-text:3--></select></label><label>Reasoning effort<select name="reasoning_effort"><option value="">Disabled</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="xhigh">XHigh</option></select></label><label class="checkbox-row"><app-checkbox name="vision" value="on"${this.#modelVision ? ' checked' : ''} data-argon="16146" data-argon-bind="0"></app-checkbox>Supports vision</label></div><div class="actions"><app-button type="submit">Add model</app-button></div></form></app-settings-section><app-settings-section title="Subagent settings" description="Control which models the main agent can use when spawning subagents, and add guidance on when to pick each one. Users without saved settings inherit the server default routing guide from config."><p class="muted">Allowed subagent models</p><div class="checkbox-list"><!--argon-text:4-->${subagentModelViews.map((model, __argonIndex) => this.#mapEventItem(`<label class="checkbox-row"><app-checkbox data-model="${model.key}"${model.checked ? ' checked' : ''} data-argon="17165"></app-checkbox><span>${model.label}</span></label>`, "initial:6:17165:" + __argonIndex, { 17165: (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked !== "boolean")
        return;
    const current = new Set(this.#agentSettings.subagent_allowed_models);
    if (checked) {
        current.add(model.key);
    }
    else {
        current.delete(model.key);
    }
    this.#setAgentSettings({
        ...this.#agentSettings,
        subagent_allowed_models: [...current],
    });
    this.#setAgentSettingsSaved(false);
} }, ["17165"])).join("")}<!--/argon-text:4--></div><label class="full skill-content">Model selection guidelines<textarea data-argon-ref="guidelinesArea" name="subagent-guidelines" placeholder="Describe when to use faster vs stronger models, cost constraints, or task-specific preferences." data-argon="18234"></textarea></label><!--argon-text:5-->${this.#agentSettings.using_server_defaults ? "<p class=\"muted\">Showing the server default from config. Save to keep your own copy; your settings will not change when admins update config.</p>" : "<p class=\"muted\">Using your saved settings.</p>"}<!--/argon-text:5--><div class="actions"><app-button data-argon="18853">Save agent settings</app-button><!--argon-text:6-->${this.#agentSettingsSaved ? "<span class=\"saved\">Saved.</span>" : this.#escText("")}<!--/argon-text:6--></div><p class="error"><!--argon-text:7-->${this.#escText(this.#agentSettingsError)}<!--/argon-text:7--></p></app-settings-section><p class="error"><!--argon-text:8-->${this.#escText(this.#error)}<!--/argon-text:8--></p>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #adoptList(start, end, htmls) {
    const specs = [];
    let node = start.nextSibling;
    for (const html of htmls) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nodes = [];
      const anchor = node;
      for (const fresh of template.content.childNodes) {
        if (!node || node === end || node.nodeType !== fresh.nodeType || (node.nodeType === 1 && node.localName !== fresh.localName)) return;
        nodes.push(node);
        node = node.nextSibling;
      }
      specs.push({ anchor, nodes });
    }
    if (node !== end) return;
    return specs.map(({ anchor, nodes }) => {
      const mark = document.createComment('argon-item');
      (anchor ?? end).before(mark);
      return [mark, ...nodes];
    });
  }

  #reconcileList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const htmls = items.map(String);
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { htmls, marks: nodes.map(nodes => nodes[0]) };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { htmls: [], marks: [] };
      }
    }
    const { htmls, marks } = list;
    if (items.length < htmls.length) {
      for (let i = items.length; i < htmls.length; i++) delete this.#eventContexts[id + ':' + i];
      let node = marks[items.length];
      while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
      htmls.length = items.length;
      marks.length = items.length;
    }
    for (let i = 0; i < items.length; i++) {
      const html = String(items[i]);
      if (i < htmls.length) {
        const stop = i + 1 < marks.length ? marks[i + 1] : end;
        if (htmls[i] === html) {
          let current = marks[i].nextSibling;
          while (current && current !== stop) { this.#wireEvents(current); current = current.nextSibling; }
          continue;
        }
        let node = marks[i].nextSibling;
        while (node && node !== stop) { const next = node.nextSibling; node.remove(); node = next; }
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        stop.before(template.content);
        htmls[i] = html;
      } else {
        const mark = document.createComment('argon-item');
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        end.before(mark, template.content);
        marks.push(mark);
        htmls.push(html);
      }
    }
  }

  #reconcileKeyedList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const keys = items.map(([key]) => key);
      const htmls = items.map(([, html]) => String(html));
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { keys, htmls, nodes };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { keys: [], htmls: [], nodes: [] };
      }
    }
    // nodes[i] = [marker comment, ...content nodes]; owned by the reconciler,
    // so item DOM never needs re-walking between updates.
    const oldKeys = list.keys, oldHtmls = list.htmls, oldNodes = list.nodes;
    const oldIndex = new Map();
    for (let i = 0; i < oldKeys.length; i++) oldIndex.set(oldKeys[i], i);
    const used = new Array(oldKeys.length).fill(false);
    const parse = (html) => {
      const template = document.createElement('template');
      template.innerHTML = html;
      this.#wireEvents(template.content);
      return template.content;
    };
    const keys = [], htmls = [], nodes = [];
    let p = 0;
    for (const [key, value] of items) {
      const html = String(value);
      while (p < oldKeys.length && used[p]) p++;
      const anchor = p < oldKeys.length ? oldNodes[p][0] : end;
      const i = oldIndex.get(key);
      if (i === undefined || used[i]) {
        const mark = document.createComment('argon-item');
        const content = parse(html);
        const fresh = [mark, ...content.childNodes];
        anchor.before(mark, content);
        nodes.push(fresh);
      } else {
        used[i] = true;
        if (i === p) p++;
        else anchor.before(...oldNodes[i]);
        if (oldHtmls[i] !== html) {
          const content = parse(html);
          const fresh = [oldNodes[i][0], ...content.childNodes];
          if (this.#patchItem(oldNodes[i], fresh)) {
            nodes.push(oldNodes[i]);
          } else {
            for (let k = 1; k < oldNodes[i].length; k++) oldNodes[i][k].remove();
            oldNodes[i][0].after(content);
            nodes.push(fresh);
          }
        } else {
          for (let k = 1; k < oldNodes[i].length; k++) this.#wireEvents(oldNodes[i][k]);
          nodes.push(oldNodes[i]);
        }
      }
      keys.push(key);
      htmls.push(html);
    }
    for (let i = 0; i < oldNodes.length; i++) {
      if (!used[i]) {
        delete this.#eventContexts[id + ':' + oldKeys[i]];
        for (const n of oldNodes[i]) n.remove();
      }
    }
    list.keys = keys;
    list.htmls = htmls;
    list.nodes = nodes;
  }

  #patchItem(current, fresh) {
    if (current.length !== 2 || fresh.length !== 2) return false;
    return this.#patchElement(current[1], fresh[1]);
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const area = this.#guidelinesArea.current;
    if (area && area.value !== this.#agentSettings.subagent_guidelines) {
        area.value = this.#agentSettings.subagent_guidelines;
    }
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const configModelViews = this.#availableModels.filter((model) => model.source === "config").map(configModelView);
    const providerViews = this.#providers.map((provider) => ({
    id: provider.id,
    name: escapeHtml(provider.name),
    meta: escapeHtml(`${provider.kind} - ${provider.url}`),
}));
    const userModelViews = this.#userModels.map(userModelItemView);
    const subagentModelViews = this.#availableModels.map((model) => subagentModelView(model, this.#agentSettings.subagent_allowed_models));
    if (prop === undefined || prop === 'availableModels' || prop === 'loaded') {
      const __argonNext = `${(configModelViews.length > 0) ? `<div class="model-list">${configModelViews.map((model) => `<div class="model-item"><div><div class="name">${model.name}</div><div class="desc">${model.meta}</div></div><app-badge variant="secondary">${model.badge}</app-badge></div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No server models are configured." : "Loading models...")}</p>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'providers' || prop === 'loaded') {
      const __argonNext = `${(providerViews.length > 0) ? `<div class="model-list">${providerViews.map((provider, __argonIndex) => this.#mapEventItem(`<div class="model-item"><div><div class="name">${provider.name}</div><div class="desc">${provider.meta}</div></div><app-button variant="outline" size="sm" data-argon="8550">Remove</app-button></div>`, "1:2:8550:" + __argonIndex, { 8550: () => {
    if (!window.confirm("Remove this provider and its models?"))
        return;
    void deleteProvider(provider.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove provider.");
    });
} }, ["8550"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No personal providers yet." : "Loading providers...")}</p>`}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'userModels' || prop === 'loaded') {
      const __argonNext = `${(userModelViews.length > 0) ? `<div class="model-list">${userModelViews.map((model, __argonIndex) => this.#mapEventItem(`<div class="model-item"><div><div class="name">${model.name}</div><div class="desc">${model.meta}</div></div><app-button variant="outline" size="sm" data-argon="12479">Remove</app-button></div>`, "2:3:12479:" + __argonIndex, { 12479: () => {
    if (!window.confirm("Remove this model?"))
        return;
    void deleteUserModel(model.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove model.");
    });
} }, ["12479"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No personal models yet." : "Loading models...")}</p>`}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'providers') {
      this.#reconcileList(3, this.#textStart3, this.#textEnd3, providerViews.map((provider) => `<option value="${provider.id}">${provider.name}</option>`));
    }
    if (prop === undefined || prop === 'availableModels' || prop === 'agentSettings') {
      this.#reconcileKeyedList(4, this.#textStart4, this.#textEnd4, subagentModelViews.map((model) => [model.key, this.#mapEventItem(`<label class="checkbox-row"><app-checkbox data-model="${model.key}"${model.checked ? ' checked' : ''} data-argon="17165"></app-checkbox><span>${model.label}</span></label>`, "4:" + model.key, { 17165: (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked !== "boolean")
        return;
    const current = new Set(this.#agentSettings.subagent_allowed_models);
    if (checked) {
        current.add(model.key);
    }
    else {
        current.delete(model.key);
    }
    this.#setAgentSettings({
        ...this.#agentSettings,
        subagent_allowed_models: [...current],
    });
    this.#setAgentSettingsSaved(false);
} }, ["17165"])]));
    }
    if (prop === undefined || prop === 'agentSettings') {
      const __argonNext = `${this.#agentSettings.using_server_defaults ? "<p class=\"muted\">Showing the server default from config. Save to keep your own copy; your settings will not change when admins update config.</p>" : "<p class=\"muted\">Using your saved settings.</p>"}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'agentSettingsSaved') {
      const __argonNext = `${this.#agentSettingsSaved ? "<span class=\"saved\">Saved.</span>" : this.#escText("")}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'agentSettingsError') {
      const __argonNext = `${this.#escText(this.#agentSettingsError)}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'modelVision') && this.#attr0) {
      this.#attr0.toggleAttribute('checked', !!(this.#modelVision));
    }
    if (this.#hooksLive && (prop === 'agentSettings')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '9858').forEach(el => this.#listen(el, '9858', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitProvider(form)
        .then(() => {
        form.reset();
        return Promise.all([
            listModels(),
            listProviders(),
            listUserModels(),
            getAgentSettings(),
        ]);
    })
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to add provider.");
    });
}));
    this.#eventTargets(scope, '13758').forEach(el => this.#listen(el, '13758', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitUserModel(form, this.#modelVision)
        .then(() => {
        form.reset();
        this.#setModelVision(false);
        return Promise.all([
            listModels(),
            listProviders(),
            listUserModels(),
            getAgentSettings(),
        ]);
    })
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to add model.");
    });
}));
    this.#eventTargets(scope, '16146').forEach(el => this.#listen(el, '16146', 'change', (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked === "boolean")
        this.#setModelVision(checked);
}));
    this.#eventTargets(scope, '18234').forEach(el => this.#listen(el, '18234', 'input', (event: Event) => {
    this.#setAgentSettings({
        ...this.#agentSettings,
        subagent_guidelines: (event.target as HTMLTextAreaElement).value,
    });
    this.#setAgentSettingsSaved(false);
}));
    this.#eventTargets(scope, '18853').forEach(el => this.#listen(el, '18853', 'click', () => {
    this.#setAgentSettingsError("");
    this.#setAgentSettingsSaved(false);
    void updateAgentSettings(this.#agentSettings)
        .then((settings) => {
        this.#setAgentSettings(settings);
        this.#setAgentSettingsSaved(true);
    })
        .catch((err) => {
        this.#setAgentSettingsError(err instanceof Error ? err.message : "Failed to save agent settings.");
    });
}));
    this.#eventTargets(scope, '8550').forEach(el => this.#listen(el, '8550', 'click', () => {
    if (!window.confirm("Remove this provider and its models?"))
        return;
    void deleteProvider(provider.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove provider.");
    });
}));
    this.#eventTargets(scope, '12479').forEach(el => this.#listen(el, '12479', 'click', () => {
    if (!window.confirm("Remove this model?"))
        return;
    void deleteUserModel(model.id)
        .then(() => Promise.all([
        listModels(),
        listProviders(),
        listUserModels(),
        getAgentSettings(),
    ]))
        .then(([models, providerItems, userModelItems, settings]) => {
        this.#setAvailableModels(models);
        this.#setProviders(providerItems);
        this.#setUserModels(userModelItems);
        this.#setAgentSettings(settings);
        this.#setLoaded(true);
        this.#setError("");
        this.#setAgentSettingsError("");
    })
        .catch(() => {
        this.#setError("Failed to remove model.");
    });
}));
    this.#eventTargets(scope, '17165').forEach(el => this.#listen(el, '17165', 'change', (event: Event) => {
    const checked = (event as CustomEvent<{
        checked: boolean;
    }>).detail?.checked;
    if (typeof checked !== "boolean")
        return;
    const current = new Set(this.#agentSettings.subagent_allowed_models);
    if (checked) {
        current.add(model.key);
    }
    else {
        current.delete(model.key);
    }
    this.#setAgentSettings({
        ...this.#agentSettings,
        subagent_allowed_models: [...current],
    });
    this.#setAgentSettingsSaved(false);
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
